<template>
	<div
		class="hdl-short-text-response question"
		:class="{
			'has-error': errorMap.size > 0
		}"
	>
		<div ref="rendered" v-html="question.renderedQuestion" />
		<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
			<ul>
				<li v-for="errorMessage of errorMessages" :key="errorMessage">
					{{ errorMessage }}
				</li>
			</ul>
		</div>
		<div
			class="input-container input-box-borders flex flex-row items-center"
		>
			<input
				type="text"
				:value="(response)? response : ''"
				@input="input"
				tabindex=0
				ref="textbox"
				:aria-labelledby="`${question.uuid}-label`"
				:aria-invalid="errorMap.size > 0"
				:aria-describedby="(errorMessages.length > 0)? `errs-${question.uuid}` : undefined"
				:aria-errormessage="(errorMessages.length > 0)? `errs-${question.uuid}` : undefined"
				:aria-required="question.required"
				:inputmode="inputMode"
				@keydown="keydownHandler"
			/>
			<font-awesome-icon
				:icon="inputTailIcon"
				aria-hidden="true"
				class="tail-icon"
				v-if="inputTailIcon"
			/>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedSurveyQuestionType, QuestionOptionKey, ShortTextResponseValidationType, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fa0, faAt, faCalendar, faHashtag, faLink, faPhone } from '@fortawesome/pro-regular-svg-icons';

library.add(
	faPhone, faAt, fa0, faLink, faCalendar, faHashtag
);

@Component({
	emits: ['intentionalBlur']
})
export default class ShortTextResponse extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: string | null;

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			this.response = (existingResponse)? existingResponse.text : null;
		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const textbox = this.$refs.textbox as HTMLInputElement | undefined;

		if (textbox) {
			textbox.focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		} else if (event.key == 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'up');
		} else if (event.key === 'Enter') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	getResponse(): string | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	input(event: Event): void {
		this.response = (event.target as HTMLInputElement).value;
		this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, this.response, null, null);
		}
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);

		if (error.code === SurveyResponseErrorCode.TEXT_VALIDATION) {

		}
	}

	get inputTailIcon(): [string, string] | null {
		if (this.question === null) {
			return null;
		}

		const validation = this.question.options[QuestionOptionKey.TEXT_VALIDATION];

		if (validation === undefined || validation.jsonValue === null) {
			return null;
		}

		const validationType = validation.jsonValue.validationType;

		switch(validationType) {
			case ShortTextResponseValidationType.PHONE:
				return ['far', 'phone'];
			break;
			case ShortTextResponseValidationType.EMAIL:
				return ['far', 'at'];
			break;
			case ShortTextResponseValidationType.DATE:
				return ['far', 'calendar'];
			break;
			case ShortTextResponseValidationType.FLOAT:
				return ['far', 'hashtag'];
			break;
			case ShortTextResponseValidationType.INT:
				return ['far', '0'];
			break;
			case ShortTextResponseValidationType.URL:
				return ['far', 'link'];
			break;
		}

		return null;
	}

	get inputMode(): 'numeric' | 'email' | 'tel' | 'decimal' | 'url' | undefined {
		if (this.question === null) {
			return undefined;
		}

		const validation = this.question.options[QuestionOptionKey.TEXT_VALIDATION];

		if (validation === undefined || validation.jsonValue === null) {
			return undefined;
		}

		const validationType = validation.jsonValue.validationType;

		switch(validationType) {
			case ShortTextResponseValidationType.PHONE:
				return 'tel';
			break;
			case ShortTextResponseValidationType.EMAIL:
				return 'email';
			break;
			case ShortTextResponseValidationType.FLOAT:
				return 'decimal';
			break;
			case ShortTextResponseValidationType.INT:
				return 'numeric';
			break;
			case ShortTextResponseValidationType.URL:
				return 'url';
			break;
		}

		return undefined;
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-short-text-response {
	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	.input-container {
		@apply rounded w-full bg-fill;

		&:focus-within {
			outline: none;
			--tw-border-opacity: 1;
			border: 1.5px solid var(--main);
			box-shadow: #535abb25 0 0 0 2px;
		}

		input[type="text"] {
			@apply rounded flex-1;

			padding: .35rem .5rem;
		}

		.tail-icon {
			@apply mx-2 flex-none bg-fill text-secondary;
		}
	}
}
</style>
